import React from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';

const HERO_IMG = '/images/activities.jpeg';

const benefits = [
  {
    title: 'Merlin Entertainments',
    description: 'Up to 49% discount at Merlin Entertainments — think Alton Towers, Sea Life, Madam Tussauds and more.',
    icon: 'ticket',
  },
  {
    title: 'Hyundai Cars',
    description: 'Up to £7,000 discount on a new Hyundai car through our exclusive partnership.',
    icon: 'car',
  },
  {
    title: 'Cotswold Outdoor',
    description: '15% discount at Cotswold Outdoor for all your adventure gear needs.',
    icon: 'mountain',
  },
  {
    title: 'Red Letter Days',
    description: '15% discount on Red Letter Days experiences — from driving to dining.',
    icon: 'gift',
  },
  {
    title: 'Naked Wines',
    description: '£60 discount at Naked Wines to enjoy great wines at great prices.',
    icon: 'wine',
  },
  {
    title: 'Tabler Magazine',
    description: 'Receive Tabler magazine once a quarter, plus free materials to help your Table events.',
    icon: 'book',
  },
];

const iconMap = {
  ticket: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  ),
  car: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 17h.01M16 17h.01M3 11l1.5-5A2 2 0 016.4 4h11.2a2 2 0 011.9 1.4L21 11M3 11h18M3 11v6a1 1 0 001 1h1a1 1 0 001-1v-1h12v1a1 1 0 001 1h1a1 1 0 001-1v-6" />
    </svg>
  ),
  mountain: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21l6-9 4 5 4-7 4 11H3z" />
    </svg>
  ),
  gift: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a4 4 0 00-4-4 4 4 0 00-4 4v2h8zm0 0h8V6a4 4 0 00-4-4 4 4 0 00-4 4v2zm-8 4h16v9a2 2 0 01-2 2H6a2 2 0 01-2-2v-9z" />
    </svg>
  ),
  wine: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 2h8l-1 7c0 2.2-1.8 4-4 4h-1c-2.2 0-4-1.8-4-4L8 2zM12 13v7m-3 0h6" />
    </svg>
  ),
  book: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
};

export default function MemberBenefitsPage() {
  return (
    <div data-testid="member-benefits-page">
      <SEO
        title="Member Benefits"
        description="Exclusive benefits for Round Table members including discounts at Merlin Entertainments, Hyundai, Cotswold Outdoor, Red Letter Days, Naked Wines and Tabler magazine."
        path="/member-benefits"
        keywords={['Round Table benefits', 'Merlin discount', 'Hyundai discount', 'member perks', 'Tabler magazine']}
      />
      {/* Hero */}
      <HeroSection
        image={HERO_IMG}
        title="Member Benefits"
        subtitle="Exclusive perks for Round Table members"
        fullHeight={false}
      />

      {/* Benefits */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
              Join Us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-4">
              Enhanced Benefits for Tablers
            </h2>
            <p className="text-base text-[#52525B] max-w-2xl mx-auto">
              Join 30,000 men around the world for lifelong friendships, unforgettable experiences and the chance to make a difference to those around you. Plus enjoy our enhanced benefits exclusive to Tablers too.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                data-testid={`benefit-card-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="benefit-card p-8 bg-[#F4F4F5] border border-transparent hover:border-[#F0A500]"
              >
                <div className="w-10 h-10 flex items-center justify-center text-[#F0A500] mb-4">
                  {iconMap[benefit.icon]}
                </div>
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">{benefit.title}</h3>
                <p className="text-sm text-[#52525B] leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Already a Member */}
      <section data-testid="already-member-section" className="py-20 sm:py-28 bg-[#0A0A0A] text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
            Already a Member?
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Access Your Benefits
          </h2>
          <p className="text-base text-zinc-400 mb-8">
            Contact Round Table Head Office to find out how to access these great deals or visit the members website.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:HQ@roundtable.org.uk"
              data-testid="benefits-contact-hq-btn"
              className="btn-amber inline-block text-center"
            >
              Contact Head Office
            </a>
            <a
              href="http://members.roundtable.co.uk/my-member-benefits/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="benefits-members-site-btn"
              className="inline-block px-8 py-3 border border-white/30 text-white font-bold uppercase tracking-wide text-sm hover:bg-white/10 transition-all text-center"
            >
              Members Website
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
            Not a Member Yet?
          </h2>
          <p className="text-base text-[#52525B] mb-8">
            Join Round Table and enjoy all these benefits and more. It's time to Do More.
          </p>
          <Link
            to="/membership"
            data-testid="benefits-join-cta-btn"
            className="btn-amber inline-block"
          >
            Join Round Table
          </Link>
        </div>
      </section>
    </div>
  );
}
